"use client";

import { useMemo, useSyncExternalStore } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDriversQuery, useVehiclesQuery, useZonesQuery } from "@/lib/api/queries";
import { buildDriverNameMap } from "@/lib/fleet/driver-name";
import {
  getVehicleEventsServerSnapshot,
  getVehicleEventsSnapshot,
  subscribeVehicleEvents,
} from "@/lib/notifications/event-store";
import { trackingFollowHref } from "@/lib/notifications/links";
import {
  describeVehicleEvent,
  parseVehicleEvent,
  vehicleEventSeverity,
  type VehicleEventContext,
} from "@/lib/notifications/vehicle-events";
import type { Notification } from "@/types/fleet";

/**
 * The lookups that turn an event's ids into names: vehicle label, zone name, driver name. Shared
 * by the toast listener and the notifications feed so both read the same cached lists.
 */
export function useVehicleEventContext(): VehicleEventContext {
  const { user } = useAuth();
  const { t } = useLanguage();
  const enabled = !!user;
  const vehiclesQuery = useVehiclesQuery({ enabled });
  const zonesQuery = useZonesQuery({ enabled });
  const driversQuery = useDriversQuery({ enabled });

  return useMemo<VehicleEventContext>(
    () => ({
      t,
      vehiclesById: new Map((vehiclesQuery.data ?? []).map((vehicle) => [vehicle.id, vehicle])),
      zoneNamesById: new Map((zonesQuery.data ?? []).map((zone) => [zone.id, zone.name])),
      driverNamesById: buildDriverNameMap(driversQuery.data ?? []),
    }),
    [t, vehiclesQuery.data, zonesQuery.data, driversQuery.data],
  );
}

/** The toasted vehicle events, as notifications for the feed and the badge. Newest first. */
export function useVehicleEventNotifications() {
  const context = useVehicleEventContext();
  const events = useSyncExternalStore(
    subscribeVehicleEvents,
    getVehicleEventsSnapshot,
    getVehicleEventsServerSnapshot,
  );

  const notifications = useMemo<Notification[]>(() => {
    const result: Notification[] = [];
    for (const entry of events) {
      const parsed = parseVehicleEvent(entry.raw);
      if (!parsed) continue;
      const { title, message, vehicleName } = describeVehicleEvent(entry.vehicleId, parsed, context, {
        speed: entry.speed,
      });
      result.push({
        id: entry.id,
        type: vehicleEventSeverity(parsed.type),
        kind: parsed.type,
        title,
        message,
        vehicleId: entry.vehicleId,
        vehicleName,
        timestamp: entry.occurredAt,
        read: entry.read,
        source: "vehicle",
        href: trackingFollowHref(entry.vehicleId),
        location:
          entry.lat !== undefined && entry.lon !== undefined
            ? { lat: entry.lat, lon: entry.lon }
            : undefined,
      });
    }
    return result;
  }, [events, context]);

  return { notifications };
}
